import { useNavigate } from "react-router-dom";
import { MenuTop } from '../../../components/menu/top/menu-top';
import IconButtonComponent from '../../../components/Icon/icon-button';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import '../styles/admin-page.css';
import { useCallback, useEffect, useState } from "react";
import { getPlanUsage, IRemainingUsage } from "../../../features/search/service/search-service";
import { getMe } from "../../../features/search/service/user-service";
import { useTranslation } from "../../../shared/i18n/i18n-context";
import { PageBreadcrumb } from "../../../components/page-breadcrumb/page-breadcrumb";
import { ProjectLoader } from "../../../components/project-loader/project-loader";

export const AdminPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const [planUsage, setPlanUsage] = useState<IRemainingUsage | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchPlanUsage = useCallback(async (companyId: string) => {
        try {
            const data = await getPlanUsage(companyId);
            setPlanUsage(data);
        } catch (error) {
            console.error('Error fetching plan usage:');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserData = useCallback(async () => {
        try {
            const userData = await getMe();

            if (userData && userData.companyId) {
                fetchPlanUsage(userData.companyId);
            } else {
                setLoading(false);
            }
            
        } catch (error) {
            console.error('Error fetching user data:');
            setLoading(false);
        }
    }, [fetchPlanUsage]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const getUsageColor = (percentage: number) => {
        if (percentage < 50) return '#4caf50';
        if (percentage < 80) return '#ff9800';
        return '#f44336';
    };

    const getPercentageValue = (value: any): number => {
        if (value === null || value === undefined) return 0;
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(numValue) ? 0 : numValue;
    };

    const hasValidUsageData = planUsage && 
                              planUsage.licenses && 
                              typeof planUsage.licenses.used === 'number' &&
                              typeof planUsage.licenses.total === 'number' &&
                              typeof planUsage.licenses.remaining === 'number';
    
    return (
        <div>
            <MenuTop />
            <div className="admin-page-container">
                <PageBreadcrumb
                    items={[
                        { label: menuT('home'), to: '/home' },
                        { label: t('adminPanel') }
                    ]}
                />
                <h1>{t('adminPanel')}</h1>
                
                {loading ? (
                    <div className="usage-card">
                        <ProjectLoader label={t('loadingData')} compact />
                    </div>
                ) : hasValidUsageData ? (
                    <div className="usage-card">
                        <div className="usage-header">
                            <span className="usage-label">{t('licenseUsage')}</span>
                        </div>
                        
                        <div className="usage-progress-container">
                            <div 
                                className="usage-progress-bar"
                                style={{
                                    width: `${getPercentageValue(planUsage.licenses.percentageUsed)}%`,
                                    backgroundColor: getUsageColor(getPercentageValue(planUsage.licenses.percentageUsed))
                                }}
                            />
                        </div>

                        <div className="usage-stats">
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.used}</span>
                                <span className="stat-label">{t('used')}</span>
                            </div>
                            <div className="usage-stat-divider" />
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.remaining}</span>
                                <span className="stat-label">{t('remaining')}</span>
                            </div>
                            <div className="usage-stat-divider" />
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.total}</span>
                                <span className="stat-label">{t('total')}</span>
                            </div>
                        </div>

                        <div className="usage-percentage">
                            {getPercentageValue(planUsage.licenses.percentageUsed).toFixed(1)}% {t('percentageUsed')}
                        </div>
                    </div>
                ) : null}

                <div className="admin-buttons-container">
                    <IconButtonComponent
                        label={t('listUsers')}
                        icon={<PersonIcon />}
                        onClick={() => navigate('/admin-painel/users')}
                    />

                    <IconButtonComponent
                        label={t('listConfigs')}
                        icon={<SettingsIcon />}
                        onClick={() => navigate('/admin-painel/list-configs')}
                    />

                    <IconButtonComponent
                        label={t('newUser')}
                        icon={<PersonIcon />}
                        onClick={() => navigate('/admin-painel/add-user')}
                    />
                </div>
            </div>
        </div>
    );
}
